import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import logger from '@/util/logger';
import { RecallTopicDao } from '@rock/db-admin';
import {
    type RecallTopic,
    type RecallTopicIcon,
    RecallTopicIconSchema,
    RecallTopicIdSchema,
    type Timestamp,
} from '@rock/types';
import { type FC, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RecallTopicIconGlyph } from './RecallTopicIconGlyph';

const log = logger('RecallTopicDialog');
const recallTopicDao = new RecallTopicDao();

/** A topic id reads like a catalog type's: lowercase words joined by dashes. */
const KEBAB_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * One recall topic: the id an admin gives it (immutable once created: recalls and homes hold it), its title,
 * what it covers, its icon in the app, and where it sorts. The description is read by the homeowner choosing
 * topics and by the classifier proposing them, so it names the products plainly. No delete: recalls and homes
 * reference topics.
 */
export const RecallTopicDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The topic being edited; absent for a new one. */
    topic?: RecallTopic;
    topics: RecallTopic[];
}> = ({ open, onOpenChange, topic, topics }) => {
    const [id, setId] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [order, setOrder] = useState('');
    const [icon, setIcon] = useState<RecallTopicIcon | undefined>();
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setId(topic?.id ?? '');
        setTitle(topic?.title ?? '');
        setDescription(topic?.description ?? '');
        setOrder(`${topic?.order ?? nextOrder(topics)}`);
        setIcon(topic?.icon);
    }, [open, topic, topics]);

    const idProblem = topic ? undefined : problemWithId(id.trim(), topics);
    const orderValue = Number(order);
    const canSave =
        !busy &&
        !idProblem &&
        !!title.trim() &&
        !!description.trim() &&
        order.trim() !== '' &&
        Number.isFinite(orderValue);

    const save = async () => {
        const now = new Date().toISOString() as Timestamp;
        const topicId = topic?.id ?? RecallTopicIdSchema.parse(id.trim());
        setBusy(true);
        try {
            await recallTopicDao.setRecallTopic({
                object: 'recall-topic',
                schema: 1,
                id: topicId,
                title: title.trim(),
                description: description.trim(),
                order: orderValue,
                ...(icon ? { icon } : {}),
                createdAt: topic?.createdAt ?? now,
                updatedAt: now,
            });
            toast.success(topic ? 'Topic saved' : `Topic ${topicId} created`);
            onOpenChange(false);
        } catch (err) {
            log.error('Failed to save recall topic', { err, topicId });
            toast.error('Unable to save the topic', { description: err instanceof Error ? err.message : undefined });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{topic ? `Topic ${topic.id}` : 'New recall topic'}</DialogTitle>
                    <DialogDescription>
                        A broad kind of recall homes can follow. Every approved recall under it reaches the homes
                        following it as an FYI. A new topic is proposed by the classifier from the next classification
                        on; Reclassify stale runs it over every recall, a draft or live one getting it as a suggestion.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="topic-id">ID</Label>
                        <Input
                            id="topic-id"
                            value={id}
                            onChange={(event) => setId(event.target.value)}
                            placeholder="pool-spa"
                            disabled={busy || !!topic}
                            autoComplete="off"
                            className="font-mono"
                        />
                        <div className={idProblem && id ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
                            {idProblem && id ? idProblem : 'Lowercase words joined by dashes. Cannot change later.'}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="topic-title">Title</Label>
                        <Input
                            id="topic-title"
                            value={title}
                            onChange={(event) => setTitle(event.target.value)}
                            placeholder="Pool, Spa & Sauna"
                            disabled={busy}
                            autoComplete="off"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="topic-description">Description</Label>
                        <Textarea
                            id="topic-description"
                            rows={3}
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            placeholder="Pool drain covers, above-ground pools, pool lights, hot tubs and saunas."
                            disabled={busy}
                        />
                        <div className="text-xs text-muted-foreground">
                            Shown to homeowners choosing topics, and to the classifier proposing them.
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="topic-icon">Icon</Label>
                        <Select
                            value={icon ?? ''}
                            onValueChange={(value) => setIcon(value ? RecallTopicIconSchema.parse(value) : undefined)}
                            disabled={busy}
                        >
                            <SelectTrigger id="topic-icon" className="w-56">
                                <SelectValue placeholder="Shield (default)" />
                            </SelectTrigger>
                            <SelectContent>
                                {RecallTopicIconSchema.options.map((option) => (
                                    <SelectItem key={option} value={option}>
                                        <span className="flex items-center gap-2">
                                            <RecallTopicIconGlyph icon={option} className="h-4 w-4" />
                                            {option}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="text-xs text-muted-foreground">Shown beside the topic in the app.</div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="topic-order">Order</Label>
                        <Input
                            id="topic-order"
                            type="number"
                            value={order}
                            onChange={(event) => setOrder(event.target.value)}
                            disabled={busy}
                            className="w-28"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={() => void save()} disabled={!canSave}>
                        {busy ? 'Saving…' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

function problemWithId(id: string, topics: RecallTopic[]): string | undefined {
    if (!id) return 'Enter an id';
    if (!KEBAB_ID.test(id) || !RecallTopicIdSchema.safeParse(id).success)
        return 'Lowercase letters, digits and dashes only';
    if (topics.some((topic) => topic.id === id)) return 'A topic with this id already exists';
    return undefined;
}

/** A new topic sorts after every existing one. */
function nextOrder(topics: RecallTopic[]): number {
    return topics.reduce((max, topic) => Math.max(max, topic.order + 1), 0);
}
